import Numeral from 'numeral';
import { createContext, useContext, useCallback } from 'react';
import { safeCharacterName } from '../../../../../../shared/common/utils';
import { PossibleStreamTooltip } from '../../../../../../shared/frontend/components/presentational/streamPreview';
import { Stack } from '../../../../../../shared/frontend/components/stack/Stack';
import { useAllDungeons, useFeatures, useSiteConfig } from '../../../../../../shared/frontend/scripts/selectors/global';
import {
  LEADERBOARD_AD_INTERVAL,
  LeaderboardInterstitialContent,
  shouldShowLeaderboardInterstitial
} from '../../../../../../shared/frontend/components/ads/leaderboardInterstitial';
import { SEASONS_BY_EXACT_SLUGS } from '../../../../shared/enums';
import { namedRoutes, resolveRoutePath } from '../../bundles/routes';
import { Avatar } from '../../../../../../shared/frontend/components/avatar/Avatar';
import { ClassIcon } from '../../../../../../shared/frontend/scripts/wow/components/ClassIcon';
import { DataTable } from '../../../../../../shared/frontend/components/tables/dataTable/DataTable';
import { IDataTableProps, TColumn } from '../../../../../../shared/frontend/components/tables/types';
import { GuildRank } from '../../components/presentational/guildRank';
import { PossibleLink } from '../../../../../../shared/frontend/components/link';
import { TKeystoneCharacterRankingsClientState, TKeystoneRankingCharacter, TRegionSummary } from '../../libs/store';
import { MythicDungeonRunIcon } from '../../mythicRuns/components/MythicDungeonRunIcon';
import { getSeasonAnchor } from '../utils';
import { t } from 'i18next';

interface SpecRankingsTableProps extends Omit<IDataTableProps<TKeystoneRankingCharacter>, 'columns' | 'data'> {
  data: TKeystoneCharacterRankingsClientState;
}

type ContextType = { ui: any; region: TRegionSummary };

const SpecRankingsTableContext = createContext<ContextType>(null as unknown as ContextType);
export const useSpecRankingsTableContext = () => {
  const context = useContext(SpecRankingsTableContext);
  if (!context) {
    throw new Error('useSpecRankingsTableContext must be used within a SpecRankingsTable');
  }
  return context;
};

const COLUMNS: TColumn<TKeystoneRankingCharacter>[] = [
  { accessor: 'Rank', header: 'Rank', width: '1px', cellRender: (row) => <GuildRank inheritSize rank={row.rank} /> },
  { accessor: 'Character', header: 'Character', cellRender: (row) => <CharacterColumn entry={row} /> },
  { accessor: 'runs', header: 'Runs', width: '100%', cellRender: (row) => <RunsColumn entry={row} /> },
  { accessor: 'Score', header: 'Score', align: 'right', cellRender: (row) => <ScoreColumn entry={row} /> }
];

export const SpecRankingsTable = ({ data, ...restProps }: SpecRankingsTableProps) => {
  const currentFeatures = useFeatures();
  const siteConfig = useSiteConfig();

  // Determine if interstitials should show (respects no_ads and provider checks)
  const totalRows = data.rankedCharacters?.length || 0;
  const showInterstitials = shouldShowLeaderboardInterstitial(LEADERBOARD_AD_INTERVAL, totalRows, currentFeatures, siteConfig) !== false;

  const renderInterstitialContent = useCallback(
    (offset: number) => <LeaderboardInterstitialContent slotIndex={offset} seed={siteConfig.seed || 0} />,
    [siteConfig.seed]
  );

  return (
    <SpecRankingsTableContext.Provider value={{ ui: data.ui, region: data.region }}>
      <DataTable
        columns={COLUMNS}
        className="slds-table rio-rankings-table rio-full-height"
        data={data.rankedCharacters || []}
        emptyContent={
          <span className="slds-p-around--large slds-text-align--center slds-text-heading--medium">
            {t('common:errors.empty-character-leaderboard')}
          </span>
        }
        containerClassName="rio-full-height"
        wrapperProps={{ className: 'rio-full-height' }}
        showInterstitialEvery={showInterstitials ? LEADERBOARD_AD_INTERVAL : undefined}
        renderInterstitialContent={showInterstitials ? renderInterstitialContent : undefined}
        {...restProps}
      />
    </SpecRankingsTableContext.Provider>
  );
};

const ScoreColumn = ({ entry }: { entry: TKeystoneRankingCharacter }) => {
  const { ui } = useSpecRankingsTableContext();
  const seasonAnchor = getSeasonAnchor(ui.season);

  return (
    <PossibleLink
      allowLink={!entry.character.anonymized}
      to={
        resolveRoutePath(namedRoutes.CharacterDetails, {
          region: entry.character.region.slug,
          realm: entry.character.realm.slug,
          character: entry.character.name
        }) + seasonAnchor.query
      }
      className="rio-realm-link"
      style={{ color: entry.scoreColor }}
    >
      <b>{Numeral(entry.score).format('0.0')}</b>
    </PossibleLink>
  );
};

const RunsColumn = ({ entry }: { entry: TKeystoneRankingCharacter }) => {
  const { ui } = useSpecRankingsTableContext();
  const dungeons = useAllDungeons();
  const season = SEASONS_BY_EXACT_SLUGS[ui.season];

  return (
    <Stack wrap spacingInRems={2} style={{ minHeight: 21 }} justifyContent="center">
      {entry.runs.map((run: any) => {
        const dungeon = dungeons.find((d) => d.id === run.zoneId)!;
        return (
          <MythicDungeonRunIcon
            key={run.keystoneRunId}
            isSpaced={false}
            size="x-small"
            dungeon={dungeon}
            seasonInfo={season}
            runs={entry.runs}
            isAlternate={false}
          />
        );
      })}
    </Stack>
  );
};

const CharacterColumn = ({ entry }: { entry: TKeystoneRankingCharacter }) => {
  const { ui } = useSpecRankingsTableContext();
  const seasonAnchor = getSeasonAnchor(ui.season);

  return (
    <div className="slds-tile slds-media">
      <Stack alignItems="center" justifyContent="space-between">
        <Avatar
          border
          size={20}
          // className={`class-bordercolor--${entry.character.class.slug}`}
          // style={{ boxSizing: 'content-box' }}
        >
          <ClassIcon class={entry.character.class.slug} size={18} />
        </Avatar>
        <div className="slds-media__body">
          <span className={`slds-truncate ${entry.character.stream ? 'streaming-now' : ''}`}>
            <PossibleStreamTooltip stream={entry.character.stream} hide={entry.character.anonymized}>
              <PossibleLink
                allowLink={!entry.character.anonymized}
                to={
                  resolveRoutePath(namedRoutes.CharacterDetails, {
                    region: entry.character.region.slug,
                    realm: entry.character.realm.slug,
                    character: entry.character.name
                  }) + seasonAnchor.query
                }
                className={`class-color--${entry.character.class.id}`}
              >
                {safeCharacterName(entry.character.name)}
              </PossibleLink>
            </PossibleStreamTooltip>
          </span>
        </div>
      </Stack>
    </div>
  );
};
